import { Tooltip, TooltipContent, TooltipTrigger, TruncatedText } from "@istari-frontend/ui";
import { Router } from "lucide-react";

const MAX_VISIBLE_TAGS = 4;

// Structural subset of the SDK ControlTag so domain models can render here too.
export interface OverlappingTagListItem {
  id: string;
  name: string;
  displayColor?: string | null;
  upstreamRemoteInfo?: object | null;
}

export function OverlappingTagList({
  tags,
  showTooltip = true,
}: {
  tags: OverlappingTagListItem[];
  showTooltip?: boolean;
}) {
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = Math.max(0, tags.length - MAX_VISIBLE_TAGS);
  const tooltipTitle = tags.map((tag) => tag.name).join(", ");
  const hasMultiple = tags.length > 1;

  if (tags.length === 1) {
    const tag = tags[0];
    return (
      <span
        className="inline-flex w-fit max-w-[180px] min-w-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
        style={{ backgroundColor: tag.displayColor ?? "#6b7280" }}
      >
        {tag.upstreamRemoteInfo && <Router className="size-2.5 shrink-0" style={{ color: "hsl(var(--foreground))" }} />}
        <TruncatedText tooltipText={tag.name} />
      </span>
    );
  }

  const content = (
    <span className="flex items-center -space-x-1.5">
      {visibleTags.map((tag, idx) => (
        <span
          key={tag.id}
          className="border-background flex size-5 items-center justify-center rounded-full border-2 text-[9px] font-semibold text-white"
          style={{
            backgroundColor: tag.displayColor ?? "#6b7280",
            zIndex: visibleTags.length - idx,
          }}
        >
          {tag.name.charAt(0).toUpperCase()}
        </span>
      ))}
      {overflowCount > 0 && (
        <span className="bg-muted text-muted-foreground border-background z-0 flex size-5 items-center justify-center rounded-full border-2 text-[9px] font-semibold">
          +{overflowCount}
        </span>
      )}
    </span>
  );

  if (!showTooltip || !hasMultiple) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{tooltipTitle}</TooltipContent>
    </Tooltip>
  );
}
